/**
 * Global free-sample reconciler.
 * Runs on every page (the cart included, where the PDP sample flow does not
 * load). Whenever the cart holds sample items but none is free, it re-scopes
 * the price rule to the first sample, applies the discount cookie, then
 * re-writes the cart so the discount attaches, verifying and retrying long
 * enough to outlast Netlify cold starts.
 */
(function () {
  'use strict';

  var NETLIFY_BASE = 'https://willowy-paprenjak-646e0e.netlify.app/.netlify/functions/sample';
  var DISCOUNT_CODE = 'FREESAMPLE';
  var MAX_ATTEMPTS = 8;
  var RETRY_MS = 3000;
  var SAMPLE_RE = /sample|tester|100ml|a6/i;

  var SECTIONS = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
  ];

  var inFlight = false;

  /* ── Pending-free display layer ──
   *
   * The £2→£0 flick the customer could see between adding a free sample and
   * the discount attaching is a display problem, not a data one: the outcome
   * is deterministic (exactly £2 off that line). So while a free claim is
   * settling, any render of that line is patched to its final numbers — the
   * real discount then lands underneath with identical values and nothing
   * visibly changes. If the claim ultimately fails, the flag self-expires
   * and one fresh render restores the true prices, so checkout never
   * disagrees with what was shown for more than the expiry window.
   *
   * The flag is written by sample-core/flow/quick-buy at press time
   * (localStorage, so it survives the navigation to /cart) and cleared here
   * the moment the cart really shows the line free.
   */
  var PENDING_KEY = 'knp_pending_free';
  var PENDING_TTL = 45000; // outlasts the full reconciler retry window
  var pendingTimer = null;

  function readPending() {
    try {
      var p = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
      if (!p || !p.v) return null;
      if (Date.now() - p.ts > PENDING_TTL) {
        localStorage.removeItem(PENDING_KEY);
        // One true render so an expired (= failed) claim stops showing £0
        freshRender();
        return null;
      }
      return p;
    } catch (e) { return null; }
  }

  function clearPending() {
    try { localStorage.removeItem(PENDING_KEY); } catch (e) {}
  }

  function patchMoney(el, delta) {
    var value = parseFloat((el.textContent || '').replace(/[^0-9.]/g, ''));
    if (isNaN(value)) return 0;
    var next = Math.max(0, value + delta);
    if (next === value) return 0;
    el.textContent = '£' + next.toFixed(2);
    return value - next;
  }

  function patchPendingDisplay() {
    var p = readPending();
    if (!p) {
      if (pendingTimer) { clearInterval(pendingTimer); pendingTimer = null; }
      return;
    }

    var lines = [].slice.call(document.querySelectorAll('[data-product-id^="' + p.v + ':"]'));

    // Drawer line items carry no variant attribute: fall back to the first
    // sample-titled line in any cart container that produced no id match
    ['.cart-drawer', '.thb-cart-form'].forEach(function (sel) {
      var root = document.querySelector(sel);
      if (!root) return;
      var covered = lines.some(function (l) { return root.contains(l); });
      if (covered) return;
      var sample = [].slice.call(root.querySelectorAll('.product-cart-item')).filter(function (item) {
        return SAMPLE_RE.test(item.textContent || '');
      })[0];
      if (sample) lines.push(sample);
    });

    lines.forEach(function (line) {
      line.querySelectorAll('.amount').forEach(function (amount) {
        // Only lines still showing the undiscounted price need patching;
        // once the real discount renders, this is a no-op
        var taken = patchMoney(amount, -2);
        if (!taken) return;
        // Keep the totals in step — scoped to this line's own container:
        // on the cart page the drawer holds a second copy of the line, and
        // unscoped totals would be subtracted once per copy
        var root = line.closest('.thb-cart-form, .cart-drawer') || document;
        root.querySelectorAll('.cart-total .amount, [class*="subtotal"] .amount').forEach(function (total) {
          patchMoney(total, -taken);
        });
      });
    });

    if (!pendingTimer) {
      // Renders come from several sources (add response, drawer open, our
      // own re-writes); re-assert on a short tick rather than chasing them
      pendingTimer = setInterval(patchPendingDisplay, 150);
    }
  }

  /* ── Checkout-time discount application ──
   *
   * The attach step (cookie, then a cart re-write) can lose a race against a
   * customer who heads straight for checkout: the code is valid and unused,
   * but never made it onto the cart, so they pay £2 for a promised-free
   * sample. Order #8404 was exactly that.
   *
   * /discount/CODE?redirect=/checkout applies the code server-side during the
   * navigation itself, so it cannot be raced by anything happening in the
   * browser. This routes the checkout button through it.
   *
   * Fails OPEN in every uncertain case. Blocking checkout would cost far more
   * than the £2 this recovers, so anything unexpected falls through to the
   * button's normal behaviour.
   */
  var cartSnapshot = null; // { hasFreeSample, hasPaidSample } from the last read
  var checkoutRedirecting = false;

  function snapshotCart(cart) {
    try {
      var samples = cart.items.filter(function (item) {
        return SAMPLE_RE.test(item.variant_title || '');
      });
      cartSnapshot = {
        hasPaidSample: samples.some(function (i) { return i.final_line_price > 0; }),
        hasFreeSample: samples.some(function (i) { return i.final_line_price === 0; })
      };
    } catch (e) {
      cartSnapshot = null;
    }
  }

  function storedCode() {
    try {
      var c = localStorage.getItem('knp_code');
      // Only personal codes: the bare shared code is the legacy backstop and
      // is re-scoped constantly, so sending someone to checkout on it could
      // apply someone else's colour or nothing at all
      return c && c.indexOf(DISCOUNT_CODE + '-') === 0 ? c : '';
    } catch (e) { return ''; }
  }

  function onCheckoutClick(e) {
    if (checkoutRedirecting) return;

    var btn = e.target.closest && e.target.closest('[name="checkout"]');
    if (!btn) return;

    // Unknown cart state, no personal code, nothing to rescue, or the
    // discount already landed: leave checkout completely alone
    var code = storedCode();
    if (!code) return;
    if (!cartSnapshot || !cartSnapshot.hasPaidSample || cartSnapshot.hasFreeSample) return;

    checkoutRedirecting = true;
    e.preventDefault();
    e.stopPropagation();

    window.location.href = '/discount/' + encodeURIComponent(code) +
      '?redirect=' + encodeURIComponent('/checkout');

    // If the navigation is blocked for any reason, restore normal checkout
    // rather than stranding the customer on the cart
    setTimeout(function () {
      if (!checkoutRedirecting) return;
      checkoutRedirecting = false;
      var form = btn.form;
      if (form) form.submit();
    }, 2500);
  }

  // Capture phase: the theme binds its own submit handling on these buttons
  document.addEventListener('click', onCheckoutClick, true);

  function memberEmail() {
    try {
      if (localStorage.getItem('knp_member') !== 'true') return '';
      return localStorage.getItem('knp_email') || '';
    } catch (e) { return ''; }
  }

  function getCart() {
    // no-store: this read decides whether the discount attached; a cached
    // /cart.js response (common on back/forward navigations) must never be
    // allowed to answer that
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Keep the checkout-button decision fed with fresh state
        snapshotCart(cart);
        return cart;
      });
  }

  function claim(email, variantId) {
    return fetch(NETLIFY_BASE + '/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, variant_id: variantId })
    }).then(function (r) { return r.json(); }).catch(function () {});
  }

  function applyDiscount(code) {
    // Prefer the single-use code the claim just issued; the shared code is
    // only the fallback when the claim failed outright
    return fetch('/discount/' + (code || DISCOUNT_CODE), { credentials: 'same-origin', redirect: 'manual' })
      .catch(function () {});
  }

  function renderSections(sections) {
    if (!sections) return;
    SECTIONS.forEach(function (s) {
      var el = document.getElementById(s.id);
      var html = sections[s.section];
      if (!el || !html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      if (s.id === 'cart-drawer-toggle') {
        // Replace only the count bubble: overwriting the whole toggle wipes
        // out the cart icon (the bubble is absent while the cart is empty)
        var fresh = doc.querySelector('.thb-item-count');
        if (fresh) {
          var existing = el.querySelector('.thb-item-count');
          if (existing) existing.replaceWith(fresh);
          else el.appendChild(fresh);
        }
        return;
      }
      var inner = doc.querySelector(s.selector || '.shopify-section');
      if (inner) el.innerHTML = inner.innerHTML;
    });
  }

  function attach(itemKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: itemKey,
        quantity: 1,
        sections: SECTIONS.map(function (s) { return s.section; }),
        sections_url: window.location.pathname
      }),
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (resp) { renderSections(resp.sections); })
      .catch(function () {});
  }

  var repaintedOnce = false;

  /**
   * Re-render the cart sections from a fresh request. The attach write's own
   * response renders its sections before the discount materialises, so the
   * post-attach paint must come from a follow-up render, not that response.
   * Also used once on page load when the cart healed elsewhere before this
   * page was served.
   */
  function freshRender() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        sections: SECTIONS.map(function (s) { return s.section; }),
        sections_url: window.location.pathname
      }),
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (resp) { renderSections(resp.sections); })
      .catch(function () {});
  }

  function reconcile(attempt) {
    attempt = attempt || 0;
    if (inFlight) return;

    var email = memberEmail();
    if (!email) return;

    inFlight = true;

    function release() { inFlight = false; }
    function retry() {
      release();
      if (attempt < MAX_ATTEMPTS) {
        setTimeout(function () { reconcile(attempt + 1); }, RETRY_MS);
      }
    }

    getCart()
      .then(function (cart) {
        var samples = cart.items.filter(function (item) {
          return SAMPLE_RE.test(item.variant_title || '');
        });
        var hasFree = samples.some(function (item) { return item.final_line_price === 0; });

        if (hasFree) {
          // The discount is truly on the cart now: stop patching and let
          // real renders through (they carry the same numbers)
          clearPending();
          if (repaintedOnce) { release(); return; }
          repaintedOnce = true;
          return freshRender().then(release, release);
        }
        if (!samples.length) { release(); return; }

        var target = samples[0];
        return claim(email, target.variant_id)
          .then(function (result) {
            return applyDiscount(result && result.discount_code);
          })
          .then(function () { return attach(target.key); })
          .then(getCart)
          .then(function (after) {
            var freeNow = after.items.some(function (item) { return item.final_line_price === 0; });
            if (freeNow) {
              freshRender().then(release, release);
            } else {
              retry();
            }
          })
          .catch(retry);
      })
      .catch(release);
  }

  window.knpReconcile = reconcile;

  /**
   * reconcile() bails before reading the cart when there's no member email,
   * which would leave the checkout-button decision with no state to act on.
   * Anyone holding a personal code needs that state, so read it for them
   * regardless. Costs nothing for the majority, who have no code.
   */
  function refreshSnapshot() {
    if (!storedCode()) return;
    getCart().catch(function () {});
  }

  ['cart:item-added', 'cart:refresh', 'line-item:change:end', 'cart-drawer:open'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      patchPendingDisplay(); // synchronously, before the flick can paint
      refreshSnapshot();
      setTimeout(function () { reconcile(0); }, 600);
    });
  });

  window.addEventListener('pageshow', function () { patchPendingDisplay(); refreshSnapshot(); reconcile(0); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { patchPendingDisplay(); refreshSnapshot(); reconcile(0); });
  } else {
    patchPendingDisplay();
    refreshSnapshot();
    reconcile(0);
  }
})();
